import React from "react";
import { X, Check } from "lucide-react";

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationIds: string[];
}

interface Organization {
  id: string;
  name: string;
}

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: UserFormData) => void;
  formData: UserFormData;
  setFormData: (data: UserFormData) => void;
  roleOptions: { value: string; label: string }[];
  organizations: Organization[];
  isEditMode?: boolean; // 新增编辑模式标识
  editingUserId?: string; // 新增编辑用户ID
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  roleOptions,
  organizations,
  isEditMode = false, // 默认为添加模式
  editingUserId,
}) => {
  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const toggleOrganization = (orgId: string) => {
    const newOrgIds = formData.organizationIds.includes(orgId)
      ? formData.organizationIds.filter((id) => id !== orgId)
      : [...formData.organizationIds, orgId];
    setFormData({ ...formData, organizationIds: newOrgIds });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {isEditMode ? "Edit User" : "Add New User"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  disabled={isEditMode} // 编辑模式下邮箱不可修改
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isEditMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
                {isEditMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    Email cannot be changed
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.role === "Partner Admin"
                    ? "Partner Admin has full access to manage users, organizations, sites, and devices"
                    : formData.role === "Partner Tech"
                    ? "Partner Tech can create new customer, add site and devices, modify alert rules, view alert data"
                    : "Partner Viewer has read-only access to view organizations, sites, devices, and alert data"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {organizations.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">
                      No customers available
                    </p>
                  ) : (
                    organizations.map((org) => (
                      <label
                        key={org.id}
                        className="flex items-center space-x-2 py-1 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={formData.organizationIds.includes(org.id)}
                            onChange={() => toggleOrganization(org.id)}
                            className="sr-only"
                          />
                          <div
                            className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                              formData.organizationIds.includes(org.id)
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300"
                            }`}
                          >
                            {formData.organizationIds.includes(org.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-gray-700">
                          {org.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select the customers this user will have access to
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {isEditMode ? "Update User" : "Add User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
